'use client';

import { useEffect, useState, useCallback } from 'react';
import { useRouter } from 'next/navigation';
import { useTranslation } from '@/hooks/useTranslation';
import { PageState } from '@/components/common/feedback/PageState';
import {
  getFinancialLogs,
  FinancialLogsResponse,
  AuditAction,
  getActionLabel,
  getActionColor,
  getStatusLabel,
  getStatusColor,
  formatModuleName,
} from '@/services/api/audit';
import { ApiClientError } from '@/lib/api-client';
import {
  DollarSign,
  Calendar,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  Clock,
  AlertTriangle,
  Filter,
  X,
} from 'lucide-react';

/**
 * 财务审计页面
 */
export default function AuditFinancialPage() {
  const router = useRouter();
  const { t } = useTranslation();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<FinancialLogsResponse | null>(null);
  const [showFilters, setShowFilters] = useState(false);

  // 筛选条件 - 财务日志必须有时间范围
  const [filters, setFilters] = useState(() => {
    const end = new Date();
    const start = new Date();
    start.setMonth(start.getMonth() - 1); // 默认最近一个月
    return {
      startDate: start.toISOString().split('T')[0],
      endDate: end.toISOString().split('T')[0],
      userId: '',
      action: '' as AuditAction | '',
      module: '',
      page: 1,
      limit: 20,
    };
  });

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const result = await getFinancialLogs({
        startDate: `${filters.startDate}T00:00:00.000Z`,
        endDate: `${filters.endDate}T23:59:59.999Z`,
        userId: filters.userId || undefined,
        action: filters.action || undefined,
        module: filters.module || undefined,
        page: filters.page,
        limit: filters.limit,
      });

      setData(result);
    } catch (err) {
      console.error('Failed to load financial logs:', err);
      if (err instanceof ApiClientError) {
        setError(err.message);
      } else {
        setError('加载财务审计日志失败');
      }
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const updateFilter = (key: string, value: string | number | undefined) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
      page: key === 'page' ? (value as number) : 1,
    }));
  };

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // 财务相关操作
  const financialActions = [
    AuditAction.PAYMENT,
    AuditAction.REFUND,
    AuditAction.INVOICE_GENERATE,
    AuditAction.FINANCIAL_CLOSE,
    AuditAction.BUDGET_APPROVE,
    AuditAction.APPROVE,
    AuditAction.REJECT,
  ];

  return (
    <div className="p-6 space-y-6">
      {/* 顶部操作栏 */}
      <div className="flex items-center justify-end space-x-3">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center px-4 py-2 rounded-lg border transition-colors ${
              showFilters
                ? 'bg-emerald-50 border-emerald-200 text-emerald-600'
                : 'border-gray-200 text-gray-600 hover:bg-gray-50'
            }`}
          >
            <Filter className="w-4 h-4 mr-2" />
            {'筛选'}
          </button>
      </div>

      {/* SOX 合规提示 */}
      <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-4">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
            <DollarSign className="w-5 h-5 text-emerald-600" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-emerald-900">{t.audit?.financial?.soxCompliance}</h3>
            <p className="text-sm text-emerald-700">
              {t.audit?.financial?.soxComplianceDesc}
            </p>
          </div>
        </div>
      </div>

      {/* 时间范围选择 */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center space-x-2">
            <Calendar className="w-5 h-5 text-gray-400" />
            <span className="text-sm text-gray-500">{t.audit?.financial?.timeRangeRequired}:</span>
            <input
              type="date"
              value={filters.startDate}
              onChange={(e) => updateFilter('startDate', e.target.value)}
              className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
            />
            <span className="text-gray-400">{'至'}</span>
            <input
              type="date"
              value={filters.endDate}
              onChange={(e) => updateFilter('endDate', e.target.value)}
              className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
            />
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => {
                const end = new Date();
                const start = new Date();
                start.setDate(start.getDate() - 7);
                setFilters((prev) => ({
                  ...prev,
                  startDate: start.toISOString().split('T')[0],
                  endDate: end.toISOString().split('T')[0],
                  page: 1,
                }));
              }}
              className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            >
              {t.audit?.financial?.last7Days}
            </button>
            <button
              onClick={() => {
                const end = new Date();
                const start = new Date();
                start.setMonth(start.getMonth() - 1);
                setFilters((prev) => ({
                  ...prev,
                  startDate: start.toISOString().split('T')[0],
                  endDate: end.toISOString().split('T')[0],
                  page: 1,
                }));
              }}
              className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            >
              {t.audit?.financial?.last1Month}
            </button>
            <button
              onClick={() => {
                const end = new Date();
                const start = new Date();
                start.setMonth(start.getMonth() - 3);
                setFilters((prev) => ({
                  ...prev,
                  startDate: start.toISOString().split('T')[0],
                  endDate: end.toISOString().split('T')[0],
                  page: 1,
                }));
              }}
              className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            >
              {t.audit?.financial?.last3Months}
            </button>
          </div>
        </div>
      </div>

      {/* 高级筛选 */}
      {showFilters && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">{t.audit?.financial?.advancedFilters}</h3>
            <button
              onClick={() => {
                setFilters((prev) => ({
                  ...prev,
                  userId: '',
                  action: '',
                  module: '',
                  page: 1,
                }));
              }}
              className="text-sm text-gray-500 hover:text-gray-700 flex items-center"
            >
              <X className="w-4 h-4 mr-1" />
              {t.audit?.financial?.clearFilters}
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {'操作类型'}
              </label>
              <select
                value={filters.action}
                onChange={(e) => updateFilter('action', e.target.value)}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
              >
                <option value="">{'全部'}</option>
                {financialActions.map((action) => (
                  <option key={action} value={action}>
                    {getActionLabel(action, t)}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {'模块'}
              </label>
              <input
                type="text"
                placeholder={t.audit?.financial?.enterModuleName}
                value={filters.module}
                onChange={(e) => updateFilter('module', e.target.value)}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {'用户 ID'}
              </label>
              <input
                type="text"
                placeholder={t.audit?.financial?.enterUserId}
                value={filters.userId}
                onChange={(e) => updateFilter('userId', e.target.value)}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />
            </div>
          </div>
        </div>
      )}

      {/* 统计摘要 */}
      {data?.summary && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
            <p className="text-sm text-gray-500">{t.audit?.financial?.totalRecords}</p>
            <p className="text-2xl font-bold text-gray-900 mt-1">
              {data.summary.total.toLocaleString()}
            </p>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
            <p className="text-sm text-gray-500">{t.audit?.financial?.actionDistribution}</p>
            <div className="flex flex-wrap gap-2 mt-2">
              {Object.entries(data.summary.byAction).slice(0, 3).map(([action, count]) => (
                <span
                  key={action}
                  className="inline-flex items-center px-2 py-1 rounded text-xs bg-emerald-50 text-emerald-700"
                >
                  {getActionLabel(action as AuditAction, t)}: {count}
                </span>
              ))}
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
            <p className="text-sm text-gray-500">{t.audit?.financial?.moduleDistribution}</p>
            <div className="flex flex-wrap gap-2 mt-2">
              {Object.entries(data.summary.byModule).slice(0, 3).map(([module, count]) => (
                <span
                  key={module}
                  className="inline-flex items-center px-2 py-1 rounded text-xs bg-blue-50 text-blue-700"
                >
                  {formatModuleName(module, t)}: {count}
                </span>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* 错误提示 */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center">
          <AlertTriangle className="w-5 h-5 text-red-500 mr-3" />
          <span className="text-red-700">{error}</span>
        </div>
      )}

      {/* 日志列表 */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="px-6 py-3 bg-gray-50 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-500">
              共 {data?.total || 0} 条财务操作记录
            </span>
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-500">每页</span>
              <select
                value={filters.limit}
                onChange={(e) => updateFilter('limit', parseInt(e.target.value))}
                className="px-2 py-1 border border-gray-200 rounded text-sm"
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
              </select>
              <span className="text-sm text-gray-500">条</span>
            </div>
          </div>
        </div>

        {loading && (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="w-8 h-8 text-emerald-600 animate-spin" />
          </div>
        )}

        {!loading && (!data?.items || data.items.length === 0) && (
          <div className="py-12 flex items-center justify-center">
            <PageState
              variant="empty"
              title={t.audit?.empty?.noLogs || t.common.noData}
              layout="center"
              className="border-0 bg-transparent"
            />
          </div>
        )}

        {!loading && data?.items && data.items.length > 0 && (
          <div className="divide-y divide-gray-100">
            {data.items.map((log) => {
              const actionColor = getActionColor(log.action);
              const statusColor = getStatusColor(log.status);

              return (
                <div
                  key={log.id}
                  onClick={() => router.push(`/audit/logs/${log.id}`)}
                  className="px-6 py-4 hover:bg-gray-50 cursor-pointer transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-4">
                      <div className="w-10 h-10 bg-emerald-50 rounded-lg flex items-center justify-center">
                        <DollarSign className="w-5 h-5 text-emerald-600" />
                      </div>
                      <div>
                        <div className="flex items-center space-x-2 flex-wrap gap-y-1">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${actionColor.bg} ${actionColor.text}`}>
                            {getActionLabel(log.action, t)}
                          </span>
                          <span className="text-gray-900 font-medium">
                            {formatModuleName(log.module, t)}
                          </span>
                          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${statusColor.bg} ${statusColor.text}`}>
                            {getStatusLabel(log.status, t)}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600 mt-1 line-clamp-1">
                          {log.what}
                        </p>
                        <div className="flex items-center space-x-4 mt-2 text-xs text-gray-400">
                          <span>{log.user?.displayName || log.user?.username || log.who}</span>
                          <span>•</span>
                          <span>{log.entityType}#{log.entityId.substring(0, 8)}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-col items-end">
                      <span className="text-sm text-gray-500 flex items-center">
                        <Clock className="w-4 h-4 mr-1" />
                        {formatDateTime(log.when)}
                      </span>
                      <span className="text-xs text-emerald-600 mt-1">
                        保留 {('retentionYears' in log ? log.retentionYears : 7)} 年
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* 分页 */}
        {data && data.totalPages > 1 && (
          <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between">
            <div className="text-sm text-gray-500">
              第 {data.page} / {data.totalPages} 页
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => updateFilter('page', data.page - 1)}
                disabled={!data.hasPrev}
                className={`p-2 rounded-lg border ${
                  data.hasPrev
                    ? 'border-gray-200 text-gray-600 hover:bg-gray-50'
                    : 'border-gray-100 text-gray-300 cursor-not-allowed'
                }`}
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button
                onClick={() => updateFilter('page', data.page + 1)}
                disabled={!data.hasNext}
                className={`p-2 rounded-lg border ${
                  data.hasNext
                    ? 'border-gray-200 text-gray-600 hover:bg-gray-50'
                    : 'border-gray-100 text-gray-300 cursor-not-allowed'
                }`}
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
